/**
 * Reusable modal for listing-row notes badges.
 *
 * Click intent: "Show me the notes counted on this badge." The modal renders
 * a per-artifact NotesPanel scoped to (referenceTable, referenceId),
 * which is exactly what the badge's count query covers — so the view always
 * matches the chip number.
 *
 * Used by every listing screen whose badge clicks should reveal the notes
 * for that row without navigating away from the list.
 */
import { useTranslation } from "react-i18next";

import Modal from "@components/layout/Modal";
import NotesPanel from "@components/notes/NotesPanel";
import { DBTables } from "@utils/constants/dbTables";

type RowNotesModalProps = {
  open: boolean;
  referenceTable: DBTables;
  referenceId: string;
  artifactName?: string;
  onClose: () => void;
};

const RowNotesModal: React.FunctionComponent<RowNotesModalProps> = ({
  open,
  referenceTable,
  referenceId,
  artifactName,
  onClose,
}) => {
  const { t } = useTranslation();

  // Single close affordance: footer "Close" button only. The earlier double
  // close (× in header + Close in footer) was inconsistent with the rest of
  // the codebase, where modals use one or the other — usually footer.
  return (
    <Modal open={open} className="sm:max-w-3xl">
      <div className="flex flex-col">
        <header className="border-b border-gray-200 p-4">
          <h3 className="text-lg font-bold leading-6 text-gray-900">
            {artifactName
              ? t("notes.row_modal.title_with_name", { name: artifactName })
              : t("notes.row_modal.title")}
          </h3>
        </header>
        <div className="max-h-[70vh] overflow-y-auto">
          <NotesPanel
            referenceTable={referenceTable}
            referenceId={referenceId}
            artifactName={artifactName}
          />
        </div>
        <footer className="border-t border-gray-200 p-4 flex justify-end">
          <button onClick={onClose} className="btn-primary text-sm rounded">
            {t("common.close")}
          </button>
        </footer>
      </div>
    </Modal>
  );
};

export default RowNotesModal;
